import { toRaw, isReactive, isRef, unref } from 'vue'

/**
 * Deep-clone a value that may be (or contain) Vue 3 reactive Proxies.
 *
 * `structuredClone` throws `DataCloneError` when handed a reactive Proxy, so we
 * recursively unwrap refs/reactive wrappers into plain data first. This replaces
 * the bare `structuredClone(state.plan)` calls that worked under Vue 2's
 * getter/setter reactivity but break under Vue 3's Proxy-based reactivity.
 *
 * Dates are preserved (unlike a JSON round-trip).
 */
export function rawClone<T>(value: T): T {
  return structuredClone(toPlain(value)) as T
}

function toPlain(value: unknown): unknown {
  const inner = isRef(value) ? unref(value) : value

  if (inner === null || typeof inner !== 'object') {
    return inner
  }

  if (inner instanceof Date) {
    return new Date(inner.getTime())
  }

  const source = isReactive(inner) ? toRaw(inner) : inner

  if (Array.isArray(source)) {
    return source.map((item) => toPlain(item))
  }

  const out: Record<string, unknown> = {}
  for (const key of Object.keys(source)) {
    out[key] = toPlain((source as Record<string, unknown>)[key])
  }
  return out
}
